/**
 * ActivityRow Component
 * Single event row with clock icon for timeline display
 */

import React from 'react';
import { Popup } from 'semantic-ui-react';
import { ClockStopwatch } from '@untitledui/icons';
import { I18n } from 'react-redux-i18n';
import { HealthStatus, EventKey } from '../types';
import { calculateEventStatus } from '../helpers/statusCalculators';
import { formatTimeElapsed } from '../../../../utils/time/formatTimeElapsed';
import { formatDateTime } from '../../../../utils/time/formatDateTime';
import { getEventStatusLabel } from '../constants';
import StatusDot from './StatusDot';
import styles from '../SystemHealth.module.css';

const I18N_PREFIX = 'systemSettings.systemHealth';

interface ActivityRowProps {
  eventKey: EventKey;
  label: string;
  timestamp: string | null;
}

export const ActivityRow: React.FC<ActivityRowProps> = ({
  eventKey,
  label,
  timestamp
}) => {
  const status = calculateEventStatus(eventKey, timestamp);
  const timeElapsed = timestamp 
    ? formatTimeElapsed(timestamp, undefined, { isUtc: true, maxUnits: 2 })
    : I18n.t(`${I18N_PREFIX}.time.never`);
  const fullTimestamp = timestamp 
    ? formatDateTime(timestamp, { includeTime: true })
    : I18n.t(`${I18N_PREFIX}.time.noData`);
  const thresholdTooltip = I18n.t(`${I18N_PREFIX}.thresholds.${eventKey}`);

  return (
    <div className={styles.activityRow}>
      {/* Timeline connector */}
      <div className={styles.timelineConnector}>
        <Popup
          trigger={
            <div className={styles.clockIcon}>
              <ClockStopwatch size={16} />
            </div>
          }
          content={thresholdTooltip}
          position="left center"
          size="mini"
          inverted
        />
      </div>

      {/* Event content */}
      <div className={styles.activityContent}>
        <span className={styles.activityLabel}>{label}</span>
        <Popup
          trigger={
            <span className={styles.activityTime}>{timeElapsed}</span>
          }
          content={fullTimestamp}
          position="top center"
          size="mini"
          inverted
        />
        <div className={styles.activityStatus}>
          <StatusDot status={status} size="small" showPulse={false} />
          <span className={`${styles.activityStatusText} ${styles[`status${status.charAt(0).toUpperCase() + status.slice(1)}`]}`}>
            {getEventStatusLabel(status)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ActivityRow;
